import React from 'react';

import {
  IonApp,
  IonRouterOutlet,
  setupIonicReact,
  useIonToast,
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';
import '@ionic/react/css/display.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/float-elements.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/typography.css';

import clsx from 'clsx';
import { Redirect, Route } from 'react-router-dom';

import CallbackPage from './auth/CallbackPage/CallbackPage';
import GGSecurity from './auth/GGSecurity';
import GGSecurityRoute from './auth/GGSecurityRoute';
import LoginPage from './auth/LoginPage/LoginPage';
import UserProvider from './auth/User';
import { useDebug } from './components/Debug';
import Menu from './components/Menu';
import { DASHBOARD, FINANCE, PERFORMANCE, SETTINGS } from './consts';
import { useInstallPrompt } from './hooks/useInstallPrompt';
import { useUnhandledErrors } from './hooks/useUnhandledErrors';
import { useUpdateApplication } from './hooks/useUpdateApplication';
import { DateFormatterProvider } from './intl';
import { ReactQueryProvider } from './query';
import DashboardPage from './scenes/DashboardPage/DashboardPage';
import FinancePage from './scenes/FinancePage/FinancePage';
import PerformancePage from './scenes/PerformancePage/PerformancePage';
import SettingsPage from './scenes/SettingsPage/SettingsPage';

/* Theme variables */
import './theme/_variables.css';
import './theme/colors.css';
import './theme/common.css';
import './theme/debug.css';
import './theme/typography-colors.css';

setupIonicReact({});

const App: React.FC = () => {
  useUnhandledErrors();
  useInstallPrompt();
  useUpdateApplication();
  const [presentToast] = useIonToast();

  const handleErrorMessage = async (message: string) => {
    await presentToast({
      cssClass: 'word-break-all',
      message: message,
      color: 'danger',
      duration: 2000,
    });
  };

  const handleSuccessMessage = async (message: string) => {
    await presentToast({
      cssClass: 'word-break-all',
      message: message,
      color: 'success',
      duration: 2000,
    });
  };

  const handleMessage = async (message: string) => {
    await presentToast({
      cssClass: 'word-break-all',
      message: message,
      duration: 2000,
    });
  };

  const { markup } = useDebug();

  return (
    <ReactQueryProvider
      showErrorMessage={handleErrorMessage}
      showSuccessMessage={handleSuccessMessage}
      showMessage={handleMessage}
    >
      <DateFormatterProvider>
        <IonApp
          className={clsx({
            debug: markup,
          })}
        >
          <IonReactRouter>
            <GGSecurity>
              <UserProvider>
                <Menu />
                <IonRouterOutlet ionPage animated={false} id={'main-content'}>
                  <Route exact path={'/callback'}>
                    <CallbackPage />
                  </Route>
                  <Route exact path={'/login'}>
                    <LoginPage />
                  </Route>
                  <Redirect exact path={'/'} to={`/${PERFORMANCE}`} />
                  <Redirect exact path={'/tabs/*'} to={`/${PERFORMANCE}`} />
                  <GGSecurityRoute
                    exact
                    path={`/${DASHBOARD}`}
                    component={DashboardPage}
                  />
                  <GGSecurityRoute
                    exact
                    path={`/${PERFORMANCE}`}
                    component={PerformancePage}
                  />
                  <GGSecurityRoute
                    path={`/${FINANCE}`}
                    component={FinancePage}
                  />
                  <GGSecurityRoute
                    exact
                    path={`/${SETTINGS}`}
                    render={() => <SettingsPage />}
                  />
                </IonRouterOutlet>
              </UserProvider>
            </GGSecurity>
          </IonReactRouter>
        </IonApp>
      </DateFormatterProvider>
    </ReactQueryProvider>
  );
};

export default App;
